import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { FunnelIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { PlayerMatchStatsDto } from '../types/match';
import { matchesAPI } from '../services/api';
import { useBatchedList } from '../hooks/useBatchedList';
import AgeGroupSelect from '../components/common/AgeGroupSelect';
import FilterAccordion from '../components/ui/FilterAccordion';
import LazyLoadTrigger from '../components/ui/LazyLoadTrigger';

type SortField = 'playerName' | 'matchesPlayed' | 'totalMinutes' | 'goals' | 'assists' | 'yellowCards' | 'redCards';
type SortDir = 'asc' | 'desc';

const Statistics: React.FC = () => {
  usePageTitle('Mérkőzés statisztika');
  const [stats, setStats] = useState<PlayerMatchStatsDto[]>([]);
  const [loading, setLoading] = useState(false);

  const [selectedAgeGroupId, setSelectedAgeGroupId] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [sortField, setSortField] = useState<SortField>('goals');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const params: { ageGroupId?: number; dateFrom?: string; dateTo?: string } = {};
      if (selectedAgeGroupId) params.ageGroupId = selectedAgeGroupId;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      const data = await matchesAPI.getStatistics(params);
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedAgeGroupId, dateFrom, dateTo]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'playerName' ? 'asc' : 'desc');
    }
  };

  const sorted = [...stats].sort((a, b) => {
    const aVal = a[sortField] ?? '';
    const bVal = b[sortField] ?? '';
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const { visible: visibleStats, hasMore, loadMore } = useBatchedList(sorted, 5);

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? (
      <ChevronUpIcon className="h-3 w-3 inline ml-0.5" />
    ) : (
      <ChevronDownIcon className="h-3 w-3 inline ml-0.5" />
    );
  };

  const thClass = 'px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Statisztika</h1>
        <p className="hidden sm:block text-sm text-gray-500 mt-1">Játékosok mérkőzés statisztikái</p>
      </div>

      <div className="mb-6">
        <FilterAccordion
          title="Szűrők"
          activeCount={(selectedAgeGroupId ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0)}
        >
          <div className="flex flex-col gap-3 p-3 bg-white rounded-lg shadow-sm md:flex-row md:flex-wrap md:items-center">
            <FunnelIcon className="hidden md:block h-5 w-5 text-gray-400" />
            <AgeGroupSelect
              value={selectedAgeGroupId}
              onChange={setSelectedAgeGroupId}
              placeholder="Összes korosztály"
            />
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Tól:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full md:w-auto px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Ig:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full md:w-auto px-2 py-1.5 border border-gray-300 rounded text-sm"
              />
            </div>
            {(selectedAgeGroupId || dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedAgeGroupId(null);
                  setDateFrom('');
                  setDateTo('');
                }}
                className="text-sm text-primary-600 hover:underline text-left"
              >
                Szűrők törlése
              </button>
            )}
          </div>
        </FilterAccordion>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : stats.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Nincs statisztika. Válassz korosztályt a szűréshez.</div>
      ) : (
        <>
          {/* Mobil kártyalista (md alatt) */}
          <div className="md:hidden">
            <div className="flex items-center gap-2 mb-3">
              <label htmlFor="mobileSort" className="text-sm text-gray-600 shrink-0">
                Rendezés:
              </label>
              <select
                id="mobileSort"
                value={sortField}
                onChange={(e) => {
                  const field = e.target.value as SortField;
                  setSortField(field);
                  setSortDir(field === 'playerName' ? 'asc' : 'desc');
                }}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
              >
                <option value="goals">Gól</option>
                <option value="assists">Gólpassz</option>
                <option value="matchesPlayed">Meccs</option>
                <option value="totalMinutes">Perc</option>
                <option value="yellowCards">Sárga lap</option>
                <option value="redCards">Piros lap</option>
                <option value="playerName">Játékos neve</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {visibleStats.map((row) => (
                <div key={row.playerId} className="bg-white rounded-lg shadow border p-3 flex flex-col">
                  <div className="font-medium text-sm text-gray-900 break-words hyphens-auto leading-snug mb-1.5">{row.playerName}</div>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs text-gray-600">
                    <div>
                      <span className="text-gray-400">Meccs: </span>
                      {row.matchesPlayed}
                    </div>
                    <div>
                      <span className="text-gray-400">Perc: </span>
                      {row.totalMinutes}
                    </div>
                    <div>
                      <span className="text-gray-400">Gól: </span>
                      <span className="font-medium text-gray-700">{row.goals}</span>
                    </div>
                    <div className="truncate">
                      <span className="text-gray-400">Gólpassz: </span>
                      {row.assists}
                    </div>
                    <div>
                      <span className="text-gray-400">Sárga: </span>
                      {row.yellowCards}
                    </div>
                    <div>
                      <span className="text-gray-400">Piros: </span>
                      {row.redCards}
                    </div>
                  </div>
                </div>
              ))}
              {hasMore && (
                <div className="col-span-2">
                  <LazyLoadTrigger key={visibleStats.length} onLoadMore={loadMore} />
                </div>
              )}
            </div>
          </div>

          {/* Desktop táblázat (md-től) */}
          <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`${thClass} text-left`} onClick={() => handleSort('playerName')}>
                    Játékos <SortIcon field="playerName" />
                  </th>
                  <th className={`${thClass} text-right`} onClick={() => handleSort('matchesPlayed')}>
                    Meccs <SortIcon field="matchesPlayed" />
                  </th>
                  <th className={`${thClass} text-right`} onClick={() => handleSort('totalMinutes')}>
                    Perc <SortIcon field="totalMinutes" />
                  </th>
                  <th className={`${thClass} text-right`} onClick={() => handleSort('goals')}>
                    Gól <SortIcon field="goals" />
                  </th>
                  <th className={`${thClass} text-right`} onClick={() => handleSort('assists')}>
                    Gólpassz <SortIcon field="assists" />
                  </th>
                  <th className={`${thClass} text-right`} onClick={() => handleSort('yellowCards')}>
                    Sárga <SortIcon field="yellowCards" />
                  </th>
                  <th className={`${thClass} text-right`} onClick={() => handleSort('redCards')}>
                    Piros <SortIcon field="redCards" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((row) => (
                  <tr key={row.playerId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.playerName}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{row.matchesPlayed}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{row.totalMinutes}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 font-medium">{row.goals}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{row.assists}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{row.yellowCards}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700">{row.redCards}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Statistics;
